import { onMounted, onUnmounted } from 'vue';

import { storeToRefs } from 'pinia';

import { useAppVersionStore } from '@/stores/appVersion';
import { useMaintenanceStore } from '@/stores/maintenance';

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const DISMISS_COOLDOWN = 30 * 60 * 1000; // 30 minutes before showing again after dismiss

export function useVersionCheck() {
  const versionStore = useAppVersionStore();
  const maintenanceStore = useMaintenanceStore();
  const { isNewVersionAvailable, loadedVersion, latestVersion } = storeToRefs(versionStore);
  let intervalId = null;
  let dismissedUntil = null;

  const clearCacheAndReload = () => versionStore.clearCachesAndReload();

  const dismissUpdate = () => {
    versionStore.dismiss();
    dismissedUntil = Date.now() + DISMISS_COOLDOWN;
  };

  const checkVersion = async () => {
    // Maintenance-флаг едет в том же тике, но не глушится кулдауном
    // version-диалога — оверлей должен появляться/сниматься всегда.
    maintenanceStore.checkMaintenance();

    if (dismissedUntil && Date.now() < dismissedUntil) {
      return;
    }
    await versionStore.checkVersion();
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      checkVersion();
    }
  };

  const handleWindowFocus = () => {
    checkVersion();
  };

  const startVersionCheck = () => {
    checkVersion();

    intervalId = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    window.addEventListener('focus', handleWindowFocus);

    document.addEventListener('visibilitychange', handleVisibilityChange);
  };

  const stopVersionCheck = () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    window.removeEventListener('focus', handleWindowFocus);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };

  onMounted(() => {
    startVersionCheck();
  });

  onUnmounted(() => {
    stopVersionCheck();
  });

  return {
    isNewVersionAvailable,
    loadedVersion,
    latestVersion,
    checkVersion,
    clearCacheAndReload,
    dismissUpdate,
  };
}
